/* ============================================================
   CONFIGURATEUR 3D ELIOBOT - bundle genere par tools/bundle_js.py
   NE PAS EDITER A LA MAIN. Sources : cfg3d-core.js / cfg3d-robot.js /
   cfg3d-ar.js / cfg3d-ui.js. Relancer `python3 tools/bundle_js.py` apres modif.
   Fichier unique = pas de sous-import a resoudre, le hash Shopify
   (asset_url) casse le cache tout seul -> aucun versionnement manuel.
   ============================================================ */
// ----- cfg3d-core.js -----
import * as THREE from 'https://esm.sh/three@0.160.0';
import { GLTFLoader } from 'https://esm.sh/three@0.160.0/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'https://esm.sh/three@0.160.0/examples/jsm/loaders/DRACOLoader.js';
import { OrbitControls } from 'https://esm.sh/three@0.160.0/examples/jsm/controls/OrbitControls.js';
import { RGBELoader } from 'https://esm.sh/three@0.160.0/examples/jsm/loaders/RGBELoader.js';
import { EffectComposer } from 'https://esm.sh/three@0.160.0/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'https://esm.sh/three@0.160.0/examples/jsm/postprocessing/RenderPass.js';
import { GTAOPass } from 'https://esm.sh/three@0.160.0/examples/jsm/postprocessing/GTAOPass.js';
import { OutputPass } from 'https://esm.sh/three@0.160.0/examples/jsm/postprocessing/OutputPass.js';

const root = document.querySelector('[data-cfg]');
// data-cfg-data peut être hors de la section (markup Design) : on cherche dans tout le document.
const DATA = JSON.parse(document.querySelector('[data-cfg-data]').textContent);

const COLORS = {
  'Gris': '#9aa0a8', 'Bleu': '#3b6fb0', 'Blanc': '#f4f3f0', 'Rouge': '#d9434b',
  'Vert': '#46a86b', 'Violet': '#7a4a9b', 'Jaune': '#f3c54a',
};
const COLOR_ORDER = Object.keys(COLORS);

// Couleurs des yeux LED (choix client).
const LED_COLORS = {
  'Bleu': '#3aa6ff', 'Cyan': '#2fe6e6', 'Vert': '#4ee85f', 'Jaune': '#ffd23a',
  'Rouge': '#ff4d5a', 'Rose': '#ff6ec7', 'Violet': '#b06bff', 'Blanc': '#dff0ff',
};
const LED_COLOR_ORDER = Object.keys(LED_COLORS);

const NODE_BASE = 'Base';
const NODE_LOWER = 'ShellLower';
const NODE_UPPER = 'ShellUpper';
const NODE_TOPCLEAR = 'ShellTopClear';
const NODE_BUTTON = 'ButtonTop';        // bouton : suit la couleur de la coque inf
const NODE_VISOR = 'Visiere';           // visiere fumee : Eliobot complet
const NODE_BACKPACK_DEF = 'BackpackDefault'; // backpack fume par defaut : Eliobot
const MAKER_NODES = ['MakerBody', 'MakerScrews']; // internes visibles en Maker seul
const TINT_NODES = [NODE_LOWER, NODE_UPPER, NODE_BUTTON]; // pieces teintees par la couleur
const BACKPACK_MOUNT = 'BackpackMount';
const DEMO = DATA.demo === true;

const state = { lower: true, upper: true, color: 'Gris', eyes: 'Amoureux', ledColor: 'Bleu', backpack: null };

function resolveVersion(s) {
  if (!s.lower && !s.upper) return 'Maker';
  if (s.lower && !s.upper) return 'Original';
  return 'Eliobot';
}
function findVariant() {
  const version = resolveVersion(state);
  const prod = DATA.products[version];
  if (!prod || !prod.variants.length) return null;
  if (version === 'Maker') return prod.variants[0];
  // Le robot a un stock commun aux 7 couleurs : une seule variante, la couleur
  // voyage en propriete de ligne. On garde la recherche par couleur au cas ou
  // un produit aurait malgre tout une option Couleur.
  return prod.variants.find(v => v.couleur === state.color) || prod.variants[0];
}
function formatMoney(cents) {
  return (cents / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

const canvas = root.querySelector('[data-cfg-canvas]');
const loaderEl = root.querySelector('[data-cfg-loader]');
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 0.8;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
// Empêche la page de scroller quand on zoome (molette/trackpad) sur la 3D.
// Sur tout le stage + stopPropagation : bloque aussi un éventuel smooth-scroll
// global du thème (ex. Pebble) qui scrollerait malgré le preventDefault.
(canvas.parentElement || canvas).addEventListener('wheel', (e) => {
  e.preventDefault(); e.stopPropagation();
}, { passive: false });
// Symetrique a droite : le menu doit scroller meme si le theme (Pebble) capte
// la molette globalement. On scrolle le panneau nous-memes et on coupe la
// propagation. Si rien a defiler (contenu plus court que la zone), on laisse
// l'evenement filer vers la page pour ne pas bloquer le scroll global.
const scrollPanel = root.querySelector('.e-cfg__scroll');
if (scrollPanel) {
  scrollPanel.addEventListener('wheel', (e) => {
    if (scrollPanel.scrollHeight <= scrollPanel.clientHeight) return;
    scrollPanel.scrollTop += e.deltaY;
    e.preventDefault();
    e.stopPropagation();
  }, { passive: false });
}

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
// Vue 3/4 avant-gauche, legerement dezoomee (avant = +Z apres rotation du modele).
camera.position.set(4.12, 2.18, 6.05);   // 3/4 avant-gauche, recule (~+10% de distance) pour un cadrage un peu plus large
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;
controls.enablePan = false;
controls.minDistance = 3;
controls.maxDistance = 14;   // marge de recul (le fit mobile dezoome au-dela de 9)
controls.zoomSpeed = 1.8;

// Reference du modele charge (renseignee par cfg3d-robot.js apres le GLB) : sert
// au fit camera mobile pour cadrer le robot sans le cliper sur les bords.
const view = { model: null };
// Lumiere douce/diffuse : remplissage hemispherique fort (ciel clair + rebond de
// sol chaud et CLAIR -> releve le dessous du robot) et directionnelle ADOUCIE
// (le "spot" du dessus est attenue) -> moins de contraste dessus-clair/dessous-noir.
scene.add(new THREE.HemisphereLight(0xffffff, 0x9a8f7c, 0.95));
const keyLight = new THREE.DirectionalLight(0xffffff, 0.22);
keyLight.position.set(2, 3, 2);
keyLight.castShadow = true;
keyLight.shadow.mapSize.set(2048, 2048);
keyLight.shadow.camera.near = 0.5;
keyLight.shadow.camera.far = 16;
keyLight.shadow.camera.left = -4.2;   // couvre les props (plante etc.) -> plus d'ombre buggee au bord
keyLight.shadow.camera.right = 4.2;
keyLight.shadow.camera.top = 4.2;
keyLight.shadow.camera.bottom = -4.2;
keyLight.shadow.bias = -0.0005;
scene.add(keyLight);
// Sol invisible qui ne reçoit que l'ombre projetée : elle suit le modèle.
// Position Y ajustee au chargement pour toucher le bas des roues.
const shadowPlane = new THREE.Mesh(
  new THREE.PlaneGeometry(8, 8),
  new THREE.ShadowMaterial({ opacity: 0.2 })
);
shadowPlane.rotation.x = -Math.PI / 2;
shadowPlane.position.y = -0.78;
shadowPlane.receiveShadow = true;
scene.add(shadowPlane);

const nodes = {};
const shellMats = [];
const gltfLoader = new GLTFLoader();
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
gltfLoader.setDRACOLoader(dracoLoader);

// Postprocessing : occlusion ambiante (GTAO) pour ancrer le modele.
// Fond transparent preserve (alpha 0) pour laisser passer le degrade CSS.
renderer.setClearColor(0x000000, 0);
const composer = new EffectComposer(renderer);
composer.addPass(new RenderPass(scene, camera));
const gtao = new GTAOPass(scene, camera, 1, 1);
gtao.output = GTAOPass.OUTPUT.Default;
gtao.updateGtaoMaterial({ radius: 0.12, distanceExponent: 1, thickness: 1, scale: 1.1, samples: 16 });
composer.addPass(gtao);
composer.addPass(new OutputPass());

function resize() {
  // Taille lue sur le stage parent (jamais transforme) et non sur le canvas :
  // en mobile le canvas subit un translate/scale CSS quand le volet s'ouvre,
  // ce qui fausserait getBoundingClientRect du canvas.
  const r = (canvas.parentElement || canvas).getBoundingClientRect();
  if (r.width === 0) return;
  const dpr = Math.min(window.devicePixelRatio, 2);
  renderer.setPixelRatio(dpr);
  renderer.setSize(r.width, r.height, false);
  composer.setPixelRatio(dpr);
  composer.setSize(r.width, r.height);
  gtao.setSize(r.width * dpr, r.height * dpr);
  camera.aspect = r.width / r.height;
  camera.updateProjectionMatrix();
  fitCameraMobile();
}
new ResizeObserver(resize).observe(canvas);

// Mobile uniquement : recule la camera pour que tout le robot tienne dans le cadre
// (portrait etroit -> on cadre sur la largeur ET la hauteur, avec marge anti-clip).
// Conserve l'angle 3/4 courant. Desktop : on garde la vue calee a la main.
const _box = new THREE.Box3(), _sph = new THREE.Sphere(), _dir = new THREE.Vector3();
function fitCameraMobile() {
  if (!view.model || !window.matchMedia('(max-width: 880px)').matches) return;
  _box.setFromObject(view.model);
  if (_box.isEmpty()) return;
  _box.getBoundingSphere(_sph);
  const vFov = camera.fov * Math.PI / 180;
  const hFov = 2 * Math.atan(Math.tan(vFov / 2) * camera.aspect);
  const dist = Math.max(_sph.radius / Math.sin(vFov / 2), _sph.radius / Math.sin(hFov / 2)) * 1.18;
  _dir.copy(camera.position).sub(controls.target).normalize();
  controls.target.copy(_sph.center);
  camera.position.copy(_sph.center).addScaledVector(_dir, dist);
  camera.updateProjectionMatrix();
  controls.update();
}

// Mobile : cale la hauteur de la carte sur l'espace REELLEMENT dispo sous le
// header du site (barre d'annonce + nav). Sinon le pied/CTA passe sous le bord
// de l'ecran. Robuste quelle que soit la hauteur du header (pas de valeur en dur).
function fitCardHeight() {
  if (!window.matchMedia('(max-width: 880px)').matches) { root.style.height = ''; return; }
  root.style.height = '';                                   // mesure la position naturelle
  // Offset RELATIF AU DOCUMENT (stable au scroll). Avec getBoundingClientRect().top
  // seul (relatif au viewport), un resize declenche pendant le scroll (barre d'URL
  // mobile qui s'affiche/se masque) donnait un top negatif -> hauteur gonflee -> la
  // carte "s'allongeait vers le bas". `+ scrollY` neutralise le scroll.
  const top = root.getBoundingClientRect().top + window.scrollY;
  root.style.height = Math.max(window.innerHeight - top, 360) + 'px';
}
fitCardHeight();
let _lastVW = window.innerWidth;
window.addEventListener('resize', () => {
  // On IGNORE les resize "hauteur seule" : sur mobile, la barre d'URL qui apparait/
  // disparait au scroll declenche un resize a chaque pixel -> recalculer la hauteur
  // faisait sauter la carte (glitch "remonte puis redescend"). On ne recalcule que
  // si la LARGEUR change (rotation, bascule desktop<->mobile).
  if (window.innerWidth === _lastVW) return;
  _lastVW = window.innerWidth;
  fitCardHeight();
});
window.addEventListener('orientationchange', () => setTimeout(() => { _lastVW = window.innerWidth; fitCardHeight(); }, 120));

function tick() {
  controls.update();
  composer.render();
  requestAnimationFrame(tick);
}
function loadEnv() {
  return new Promise(resolve => {
    if (!DATA.assets.env) return resolve();
    new RGBELoader().load(DATA.assets.env, (tex) => {
      tex.mapping = THREE.EquirectangularReflectionMapping;
      scene.environment = tex;
      resolve();
    }, undefined, () => resolve());
  });
}
function setVisible(name, visible) {
  if (nodes[name]) nodes[name].visible = visible;
}

// ----- cfg3d-scene.js -----
import { RoomEnvironment } from 'https://esm.sh/three@0.160.0/examples/jsm/environments/RoomEnvironment.js';

// ====================================================================
//  DECOR 3D - bureau d'atelier OUVERT (pas de mur -> rotation 360deg).
//  Low-poly stylise : plan de travail bois, tapis silicone bleu sous le
//  robot, feutres colores, plante, tournevis, pile de livres. Reflets via
//  RoomEnvironment (IBL) pour un rendu moins terne. Quand on passe SOUS le
//  plateau, table + tapis se fondent (transparents) pour voir le robot.
//
//  Rien ici n'est ajoute a `view.model` -> fit camera (desktop+mobile)
//  calcule sur le seul robot, cadrage inchange. Le decor est ALIGNE en Y
//  sur le bas reel du robot (box.min.y) une fois le GLB charge.
// ====================================================================

const GY = 0;                // niveau de reference local du sol (le groupe est shifte en Y au chargement)

const COL = {
  bgTop:   '#F1EADE', bgBot: '#E0CBA6',     // ciel doux ; bas = ton proche de la table (horizon fondu)
  wood:    '#D9B888',                        // plateau bois clair
  legWood: '#D7BC8A',                         // pieds bois clair naturel (style scandinave)
  mat:     '#2E86D4', matGrid: '#5BA6E6',    // tapis silicone bleu (plus vif) + grille
  cupGlass:'#EAF2F6',                         // verre du pot a feutres
  markers: ['#28C0D6','#7A5CFF','#FF5FA2','#FF8A3D','#3DD15F','#3A86FF','#FF4D4D','#FFD23A','#B06BFF','#16C3B2'],
  potBlack:'#202024',                         // pot plante (noir mat)
  leaf:    ['#74B05E','#8FCB6E','#5E9A50'],   // feuilles
  steel:   '#C2C8CF', sdHandle: '#1F7AC2',    // tournevis
  postit:  ['#FFE25A', '#FF9EC4', '#8CD3F0'], // post-it jaune / rose / bleu
};

// Echelle reelle : robot = 10 cm de long = 2.402 unites (mesure) -> 1 cm = 0.2402 u.
// Toutes les tailles des props sont exprimees en cm * U pour respecter les proportions.
const U = 0.2402;
const UP = new THREE.Vector3(0, 1, 0);
// pseudo-aleatoire deterministe (Math.random est bloque) : pour desordonner props.
const rnd = (n) => { const x = Math.sin(n * 127.1 + 11.7) * 43758.5453; return x - Math.floor(x); };

function flat(color, roughness = 0.85) {
  return new THREE.MeshStandardMaterial({ color, roughness, metalness: 0, flatShading: true });
}

function gradientTexture(top, bottom) {
  const c = document.createElement('canvas'); c.width = 4; c.height = 256;
  const ctx = c.getContext('2d');
  const g = ctx.createLinearGradient(0, 0, 0, 256);
  g.addColorStop(0, top); g.addColorStop(1, bottom);
  ctx.fillStyle = g; ctx.fillRect(0, 0, 4, 256);
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
}

function matTexture() {
  const c = document.createElement('canvas'); c.width = c.height = 512;
  const ctx = c.getContext('2d');
  ctx.fillStyle = COL.mat; ctx.fillRect(0, 0, 512, 512);
  ctx.strokeStyle = COL.matGrid; ctx.lineWidth = 1.5;
  for (let i = 48; i < 512; i += 48) {
    ctx.beginPath(); ctx.moveTo(i, 80); ctx.lineTo(i, 512); ctx.stroke();
    if (i > 80) { ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(512, i); ctx.stroke(); }
  }
  ctx.lineWidth = 2.5;                        // bac a compartiments (bord)
  for (let x = 18; x < 512 - 18; x += 38) ctx.strokeRect(x, 16, 32, 46);
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
}

// Fondu quand la camera passe SOUS le niveau de l'objet (vue par dessous) :
// transparent + depthWrite off -> on voit le robot a travers la table/tapis.
const _wp = new THREE.Vector3();
function fadeFromBelow(mesh, mats, ref) {
  const arr = Array.isArray(mats) ? mats : [mats];
  mesh.onBeforeRender = (r, s, camera) => {
    (ref || mesh).getWorldPosition(_wp);   // ref = on aligne le seuil sur un autre objet (le plateau)
    const t = THREE.MathUtils.clamp((camera.position.y - (_wp.y - 0.25)) / 0.5, 0, 1);
    const trans = t < 0.999;                  // transparent UNIQUEMENT quand on passe dessous
    arr.forEach((m) => {
      m.opacity = 0.05 + 0.95 * t;
      m.depthWrite = !trans;
      // bascule transparent<->opaque seulement au franchissement (evite recompile/frame
      // et le mauvais tri de 2 surfaces transparentes coplanaires vu de dessus).
      if (m.transparent !== trans) { m.transparent = trans; m.needsUpdate = true; }
    });
  };
}

function buildSky() {
  return new THREE.Mesh(
    new THREE.SphereGeometry(40, 24, 16),
    new THREE.MeshBasicMaterial({ map: gradientTexture(COL.bgTop, COL.bgBot), side: THREE.BackSide, fog: false })
  );
}

// Petite table ronde scandinave : plateau (INCHANGE) + 3 pieds bois fuseles evases.
function buildDesk() {
  const g = new THREE.Group();
  const h = 0.3;
  const top = new THREE.Mesh(new THREE.CylinderGeometry(4.6, 4.6, h, 64), flat(COL.wood, 0.95));
  top.position.y = GY - 0.006 - h / 2;        // dessus juste sous le tapis (plateau inchange)
  top.receiveShadow = true;
  top.renderOrder = 0;
  fadeFromBelow(top, top.material);
  g.add(top);
  // 3 pieds : fixes sous le plateau (rayon 3.5), evases vers le bas (rayon 4.9),
  // fuseles (epais en haut 0.2, fins en bas 0.11). Style scandinave (cf. ref).
  const legMat = flat(COL.legWood, 0.8);
  const rTop = 3.5, rBot = 4.9, topY = GY - h, botY = GY - 5.4;
  const _t = new THREE.Vector3(), _b = new THREE.Vector3();
  for (let i = 0; i < 3; i++) {
    const a = Math.PI / 6 + i * (Math.PI * 2 / 3);
    _t.set(rTop * Math.cos(a), topY, rTop * Math.sin(a));
    _b.set(rBot * Math.cos(a), botY, rBot * Math.sin(a));
    const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.19, _t.distanceTo(_b), 16), legMat);
    leg.quaternion.setFromUnitVectors(UP, _t.clone().sub(_b).normalize());
    leg.position.copy(_t).add(_b).multiplyScalar(0.5);
    fadeFromBelow(leg, legMat, top);          // se fondent comme le plateau (seuil = niveau du plateau)
    g.add(leg);
  }
  return g;
}

function buildMat() {
  const mat = new THREE.MeshStandardMaterial({ map: matTexture(), roughness: 0.68, metalness: 0, side: THREE.DoubleSide });
  const m = new THREE.Mesh(new THREE.PlaneGeometry(3.9, 3.1), mat);
  m.rotation.x = -Math.PI / 2;
  m.position.set(0, GY, 0.35);                // pile au niveau GY -> robot pose dessus
  m.receiveShadow = true;
  m.renderOrder = 1;                          // au-dessus de la table
  fadeFromBelow(m, mat);
  return m;
}

// Pot a feutres : verre evase 8 cm, feutres 15 cm qui s'ECARTENT vers l'exterieur
// (suivent l'ouverture) et REPOSENT au fond (plus de levitation).
function buildMarkerCup() {
  const g = new THREE.Group();
  const cup = new THREE.Mesh(
    new THREE.CylinderGeometry(3.1 * U, 2.3 * U, 8 * U, 22, 1, true),   // verre un poil plus large
    new THREE.MeshStandardMaterial({ color: COL.cupGlass, roughness: 0.12, metalness: 0, transparent: true, opacity: 0.34, side: THREE.DoubleSide })
  );
  cup.position.y = GY + 4 * U;
  g.add(cup);
  // Feutres ~10.5 cm (70% de 15), DESORDONNES (azimut/longueur/inclinaison varies),
  // poses au fond et evases sans traverser la paroi du verre.
  const n = 9;
  for (let i = 0; i < n; i++) {
    // Base ECARTEE (0.6-1.6 cm du centre), puis on incline chaque feutre juste assez
    // pour que son BORD EXTERIEUR (rayon 0.5) effleure le bord du verre (3.1) au niveau
    // du rim sans le traverser : centre vise = 2.5 cm a h=8 cm. tan = (2.5 - rBase)/8.
    const a = (i / n) * Math.PI * 2 + (rnd(i) - 0.5) * 0.8;
    const rBase = 0.6 + rnd(i + 1) * 1.0;
    const theta = Math.atan2(2.5 - rBase, 8);
    const L = (9.5 + rnd(i + 7) * 2.2) * U;         // longueurs variees (~10.5 cm moyen)
    const dir = new THREE.Vector3(Math.sin(theta) * Math.cos(a), Math.cos(theta), Math.sin(theta) * Math.sin(a));
    const pen = new THREE.Mesh(new THREE.CylinderGeometry(0.5 * U, 0.5 * U, L, 8), flat(COL.markers[i % COL.markers.length], 0.45));
    pen.quaternion.setFromUnitVectors(UP, dir);
    pen.position.set(rBase * U * Math.cos(a), GY + 0.3 * U, rBase * U * Math.sin(a)).addScaledVector(dir, L / 2);
    pen.castShadow = true;
    g.add(pen);
  }
  return g;
}

// Plante : pot noir 5 cm + feuillage facette, ~12 cm au total (taille coherente desk).
function buildPlant() {
  const g = new THREE.Group();
  const pot = new THREE.Mesh(new THREE.CylinderGeometry(2.7 * U, 2.1 * U, 5 * U, 12), flat(COL.potBlack, 0.5));
  pot.position.y = GY + 2.5 * U; pot.castShadow = true;
  g.add(pot);
  const blobs = [   // x,y,z,rayon en cm, indice couleur
    [0, 8, 0, 3.1, 0], [-2, 6.6, 0.6, 2.3, 1], [2.1, 6.9, -0.5, 2.5, 2],
    [0.4, 10.6, 0.3, 2.3, 1], [-0.6, 8.4, -2.2, 1.9, 0],
  ];
  for (const [x, y, z, r, ci] of blobs) {
    const m = new THREE.Mesh(new THREE.IcosahedronGeometry(r * U, 0), flat(COL.leaf[ci]));
    m.position.set(x * U, GY + y * U, z * U); m.castShadow = true;
    g.add(m);
  }
  return g;
}

// Tournevis 20 cm : manche 9 cm (Ø3 cm) + tige 11 cm (Ø~1 cm), couche a plat.
function buildScrewdriver() {
  const g = new THREE.Group();
  const handle = new THREE.Mesh(new THREE.CylinderGeometry(1.5 * U, 1.35 * U, 9 * U, 16), flat(COL.sdHandle, 0.4));
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.45 * U, 0.45 * U, 11 * U, 10),
    new THREE.MeshStandardMaterial({ color: COL.steel, roughness: 0.25, metalness: 0.6 }));
  shaft.position.y = -(9 * U / 2 + 11 * U / 2);  // tige alignee sous le manche
  g.add(handle, shaft);
  g.rotation.z = Math.PI / 2;                     // a plat
  g.position.y = GY + 1.5 * U;                     // repose sur le rayon du manche
  g.traverse((o) => { if (o.isMesh) o.castShadow = true; });
  return g;
}

// Post-it : 3 blocs ~7.5 cm carres EMPILES, decales et tournes (pas alignes).
function buildPostits() {
  const g = new THREE.Group();
  let y = GY;
  COL.postit.forEach((c, i) => {
    const s = (7.8 - i * 0.3) * U, h = 1.4 * U;
    const pad = new THREE.Mesh(new THREE.BoxGeometry(s, h, s), flat(c, 0.92));
    pad.position.set((rnd(i) - 0.5) * 1.7 * U, y + h / 2, (rnd(i + 5) - 0.5) * 1.7 * U); // leger decalage
    pad.rotation.y = (rnd(i + 2) - 0.5) * 0.55;   // legere rotation, pile non alignee
    pad.castShadow = true; pad.receiveShadow = true;
    g.add(pad); y += h;
  });
  return g;
}

// Telecommande IR low-poly (facon Elegoo, cf. ref Romain) : corps noir + touches
// rondes (numeros blancs, * et # rouges) + croix directionnelle bleue, OK rouge.
function buildRemote() {
  const g = new THREE.Group();
  const W = 3.5, L = 8.6, H = 0.5;             // cm (telecommande ~8.6 x 3.5 cm)
  // corps a angles arrondis (Shape extrude) : coins doux en plan + leger chanfrein sur le dessus
  const r = 0.8;                               // rayon des coins (cm)
  const x0 = -W / 2, z0 = -L / 2;
  const sh = new THREE.Shape();
  sh.moveTo((x0 + r) * U, z0 * U);
  sh.lineTo((x0 + W - r) * U, z0 * U);
  sh.quadraticCurveTo((x0 + W) * U, z0 * U, (x0 + W) * U, (z0 + r) * U);
  sh.lineTo((x0 + W) * U, (z0 + L - r) * U);
  sh.quadraticCurveTo((x0 + W) * U, (z0 + L) * U, (x0 + W - r) * U, (z0 + L) * U);
  sh.lineTo((x0 + r) * U, (z0 + L) * U);
  sh.quadraticCurveTo(x0 * U, (z0 + L) * U, x0 * U, (z0 + L - r) * U);
  sh.lineTo(x0 * U, (z0 + r) * U);
  sh.quadraticCurveTo(x0 * U, z0 * U, (x0 + r) * U, z0 * U);
  const bevel = 0.12 * U;
  const bodyGeo = new THREE.ExtrudeGeometry(sh, {
    depth: H * U - bevel, bevelEnabled: true, bevelThickness: bevel, bevelSize: bevel, bevelSegments: 2, steps: 1
  });
  bodyGeo.rotateX(-Math.PI / 2);               // pose a plat : epaisseur -> Y
  bodyGeo.computeBoundingBox();
  bodyGeo.translate(0, -bodyGeo.boundingBox.min.y, 0);   // base a y=0
  bodyGeo.computeBoundingBox();
  const bodyTop = bodyGeo.boundingBox.max.y;   // vrai sommet (depth + chanfrein)
  const body = new THREE.Mesh(bodyGeo,
    new THREE.MeshStandardMaterial({ color: '#1b1b1f', roughness: 0.3, metalness: 0.1 }));
  body.position.y = GY;
  body.castShadow = true; body.receiveShadow = true;
  g.add(body);
  const topY = GY + bodyTop;
  const btn = (x, z, r, col) => {
    const m = new THREE.Mesh(new THREE.CylinderGeometry(r * U, r * U, 0.05 * U, 14),  // quasi plats
      new THREE.MeshStandardMaterial({ color: col, roughness: 0.55, metalness: 0 }));
    m.position.set(x * U, topY + 0.02 * U, z * U); m.castShadow = true;
    g.add(m);
  };
  const white = '#ece9e4', red = '#e0392c', blue = '#2f97df';
  // grille 4x3 (z negatif = haut). Numeros blancs ; 4e rangee = * 0 # (* et # rouges).
  const cols = [-1.0, 0, 1.0], rows = [-3.1, -2.1, -1.1, -0.1];
  rows.forEach((z, ri) => cols.forEach((x, ci) =>
    btn(x, z, 0.42, (ri === 3 && ci !== 1) ? red : white)));
  // croix directionnelle (bas) : OK rouge au centre + 4 fleches bleues.
  const cz = 1.95;
  btn(0, cz, 0.58, red);
  btn(0, cz - 1.05, 0.42, blue); btn(0, cz + 1.05, 0.42, blue);
  btn(-1.05, cz, 0.42, blue);    btn(1.05, cz, 0.42, blue);
  return g;
}

function buildEnvironment() {
  // IBL doux (RoomEnvironment) : reflets + relief sur le robot et les props,
  // contre le rendu terne. Ne touche pas au ciel (MeshBasic, ignore l'env).
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  pmrem.dispose();

  const env = new THREE.Group();
  env.name = 'EnvDecor';
  env.add(buildSky(), buildDesk(), buildMat());

  // Props repartis autour du robot (360deg), poses sur la table, sans le masquer.
  // Facteur global props : a l'echelle 1:1 le robot (10 cm) paraissait perdu
  // parmi des outils enormes -> on reduit les props (proportions entre eux gardees).
  const PS = 0.7;
  const cup = buildMarkerCup();      cup.scale.setScalar(PS);     cup.position.set(-2.9, 0, -0.8);
  const postits = buildPostits();    postits.scale.setScalar(PS); postits.position.set(-0.7, 0, -3.1); postits.rotation.y = 0.4;
  const plant = buildPlant();        plant.scale.setScalar(PS);   plant.position.set(2.5, 0, -1.75);
  const sd = buildScrewdriver();     sd.scale.setScalar(PS);      sd.position.set(2.15, 1.5 * U * PS, 0.5); sd.rotation.y = -0.85;
  const remote = buildRemote();      remote.scale.setScalar(PS);  remote.position.set(-1.6, 0.012, 1.65); remote.rotation.y = 0.78; // posee pile au bout de la pointe avant-gauche du tapis
  env.add(cup, postits, plant, sd, remote);

  scene.add(env);
  // Pas de fog (il faisait fondre le robot au dezoom). A la place, une GRANDE
  // table (buildDesk 50x40) qui recule jusqu'a l'horizon + bas du ciel (bgBot)
  // assorti a la table -> bord fondu naturellement, sans toucher au robot.

  // 360deg horizontal + on peut passer DESSOUS (sans atteindre les poles exacts).
  controls.minAzimuthAngle = -Infinity;
  controls.maxAzimuthAngle = Infinity;
  controls.minPolarAngle = 0.25;
  controls.maxPolarAngle = 2.9;

  // Aligne le decor sur le bas REEL du robot (box.min.y) des que le GLB est la,
  // pour qu'Eliobot soit pose pile (plus de flottement). On decale tout le groupe.
  const align = () => {
    if (view.model) {
      const box = new THREE.Box3().setFromObject(view.model);
      if (isFinite(box.min.y)) env.position.y = box.min.y - GY;
      return;
    }
    requestAnimationFrame(align);
  };
  align();
  return env;
}

buildEnvironment();

// ----- cfg3d-robot.js -----
let backpackObj = null;
const eyeNodes = [];  // module yeux : visible sur Eliobot complet uniquement

// ---------- Yeux LED (2 matrices 8x8, vrais bitmaps du firmware Eliobot) ----------
// Source : Eliobot-Python-Library/elio.py, classe EyesMatrix.
// Chaque emotion = 128 LED : indices 0-63 = oeil DROIT, 64-127 = oeil GAUCHE
// (perception robot), chacune en row-major (ligne 0 = haut, colonne 0 = gauche).
const EYE_ORDER = ['Joyeux', 'Amoureux', 'Étonné', 'Enthousiaste', 'Neutre', 'Confus', 'Étourdi', 'Triste', 'Fatigué', 'Fâché', 'KO', 'Musique'];
const EYE_EMOTIONS = {
  'Joyeux': [0,0,0,1,0,0,0,0,0,0,1,1,1,0,0,0,1,1,1,1,1,1,1,0,0,1,1,1,1,1,0,0,0,0,1,1,1,0,0,0,0,1,1,1,1,1,0,0,0,1,0,0,0,1,0,0,0,0,0,0,0,0,0,0,0,0,0,1,0,0,0,0,0,0,1,1,1,0,0,0,1,1,1,1,1,1,1,0,0,1,1,1,1,1,0,0,0,0,1,1,1,0,0,0,0,1,1,1,1,1,0,0,0,1,0,0,0,1,0,0,0,0,0,0,0,0,0,0],
  'Amoureux': [0,0,0,0,0,0,0,0,0,1,1,0,0,1,1,0,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,0,1,1,1,1,1,1,0,0,0,1,1,1,1,0,0,0,0,0,1,1,0,0,0,0,0,0,0,0,0,0,0,0,1,1,0,0,1,1,0,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,0,1,1,1,1,1,1,0,0,0,1,1,1,1,0,0,0,0,0,1,1,0,0,0],
  'Étonné': [0,0,0,0,0,0,0,0,0,0,0,0,1,0,0,0,0,0,0,1,1,0,0,0,0,0,1,1,1,1,0,0,0,1,1,1,1,1,1,0,0,0,1,1,1,1,0,0,0,0,0,1,1,0,0,0,0,0,0,1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,1,0,0,0,0,0,0,1,1,0,0,0,0,0,1,1,1,1,0,0,0,1,1,1,1,1,1,0,0,0,1,1,1,1,0,0,0,0,0,1,1,0,0,0,0,0,0,1,0,0,0,0],
  'Enthousiaste': [0,0,0,0,0,0,0,0,0,1,1,1,1,0,0,0,0,0,1,1,1,1,0,0,0,0,0,0,1,1,1,0,0,0,0,0,0,1,1,0,0,0,0,0,1,1,1,0,0,0,1,1,1,1,0,0,0,1,1,1,1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,1,1,1,1,0,0,0,1,1,1,1,0,0,0,1,1,1,0,0,0,0,0,1,1,0,0,0,0,0,0,1,1,1,0,0,0,0,0,0,1,1,1,1,0,0,0,0,0,1,1,1,1,0],
  'Neutre': [0,0,0,0,0,0,0,0,0,0,1,1,1,1,0,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,0,0,0,0,0,0,0,0,0,0,0,1,1,1,1,0,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,0],
  'Confus': [0,0,0,1,1,0,0,0,0,0,1,0,0,1,0,0,0,0,1,0,0,1,0,0,0,0,0,0,0,1,0,0,0,0,0,0,1,0,0,0,0,0,0,1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,1,0,0,0,0,0,0,0,1,1,0,0,0,0,0,1,0,0,1,0,0,0,0,1,0,0,1,0,0,0,0,0,0,0,1,0,0,0,0,0,0,1,0,0,0,0,0,0,1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,1,0,0,0,0],
  'Étourdi': [0,0,1,1,1,1,0,0,0,1,0,0,0,0,1,0,1,0,0,1,1,1,0,1,1,0,1,0,0,0,1,0,1,0,1,0,1,0,0,1,1,0,0,1,1,0,0,1,0,1,0,0,0,0,1,0,0,0,1,1,1,1,0,0,0,0,1,1,1,1,0,0,0,1,0,0,0,0,1,0,1,0,1,1,1,0,0,1,0,1,0,0,0,1,0,1,1,0,0,1,0,1,0,1,1,0,0,1,1,0,0,1,0,1,0,0,0,0,1,0,0,0,1,1,1,1,0,0],
  'Triste': [0,0,0,0,0,0,0,0,0,0,0,0,1,1,0,0,0,0,0,1,1,1,1,0,0,0,1,1,1,1,1,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,0,0,0,0,0,0,0,0,0,0,0,1,1,0,0,0,0,0,1,1,1,1,0,0,0,0,1,1,1,1,1,0,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,0],
  'Fatigué': [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,1,1,1,1,0,0,0,1,1,1,1,1,1,0,0,1,1,0,0,1,1,0,0,1,1,0,0,1,1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,1,1,1,1,0,0,0,1,1,1,1,1,1,0,0,1,1,0,0,1,1,0,0,1,1,0,0,1,1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0],
  'Fâché': [0,0,0,0,0,0,0,0,0,0,1,1,0,0,0,0,0,1,1,1,1,0,0,0,0,1,1,1,1,1,0,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,0,0,0,0,0,0,0,0,0,0,0,0,0,1,1,0,0,0,0,0,1,1,1,1,0,0,0,1,1,1,1,1,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,0],
  'KO': [0,0,0,0,0,0,0,0,0,1,0,0,0,0,1,0,0,1,1,0,0,1,1,0,0,0,1,1,1,1,0,0,0,0,0,1,1,0,0,0,0,0,1,1,1,1,0,0,0,1,1,0,0,1,1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,1,0,0,0,0,1,0,0,1,1,0,0,1,1,0,0,0,1,1,1,1,0,0,0,0,0,1,1,0,0,0,0,0,1,1,1,1,0,0,0,1,1,0,0,1,1,0,0,0,0,0,0,0,0,0],
  'Musique': [0,0,0,0,0,0,0,0,0,0,0,0,1,1,0,0,0,0,0,0,1,1,1,0,0,0,0,0,1,0,0,0,0,0,0,0,1,0,0,0,0,0,1,1,1,0,0,0,0,0,1,1,1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,1,1,0,0,0,0,0,0,1,1,1,0,0,0,0,0,1,0,0,0,0,0,0,0,1,0,0,0,0,0,1,1,1,0,0,0,0,0,1,1,1,0,0,0,0,0,0,0,0,0,0,0,0],
};

let ledMesh = null;
let ledGlow = null;                         // halo additif (glow) autour des LED allumees
let blinkClosed = false;
const eyeOn = new THREE.Color();            // couleur LED courante (choix client)
const glowCol = new THREE.Color();
const BLACK_C = new THREE.Color(0, 0, 0);
const EYE_OFF = new THREE.Color(0x05070d);  // pixel eteint (sombre)

// Mapping grille 3D -> index firmware. Les positions JSON sont deja ordonnees
// ligne par ligne (ligne 0 = haut, colonne = x local croissant). Le modele subit
// rotation.y = PI : a l'ecran les colonnes sont donc inversees (c -> 7-c) et l'oeil
// JSON 'left' (e=0) tombe a droite = oeil GAUCHE firmware (base 64) ; 'right' (e=1)
// = oeil DROIT firmware (base 0). flags ajustables si verif visuelle l'exige.
const CALIB = { swapEyes: false, flipRows: false, flipCols: true };
function fwIndex(e, r, c) {
  const rr = CALIB.flipRows ? 7 - r : r;
  const cc = CALIB.flipCols ? 7 - c : c;
  const half = CALIB.swapEyes ? 1 - e : e;   // half 0 = oeil GAUCHE firmware (64), 1 = DROIT (0)
  return (half === 0 ? 64 : 0) + rr * 8 + cc;
}

function buildLeds(parent, data) {
  const s = data.ledSize * 0.85;  // pixel LED (cube fin ; le halo additif fait le volume)
  ledMesh = new THREE.InstancedMesh(new THREE.BoxGeometry(s, s, s),
    new THREE.MeshBasicMaterial({ toneMapped: false }), 128);
  // Halo additif -> rend les LED emissives (glow). Garde-le SOUS l'espacement
  // entre LED (~1.82x ledSize = pitch) pour que deux pixels voisins allumes ne
  // fusionnent pas : les motifs denses (Etourdi...) restent nets.
  const g = data.ledSize * 1.7;
  ledGlow = new THREE.InstancedMesh(new THREE.BoxGeometry(g, g, g),
    new THREE.MeshBasicMaterial({ transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false }), 128);
  ledGlow.renderOrder = 6;  // dessine apres la visiere fumee -> glow qui ressort
  ledMesh.frustumCulled = ledGlow.frustumCulled = false;
  const m = new THREE.Matrix4();
  let i = 0;
  for (const eye of ['left', 'right']) for (const p of data[eye]) {
    m.makeTranslation(p[0], p[1], p[2]); ledMesh.setMatrixAt(i, m); ledGlow.setMatrixAt(i, m); i++;
  }
  ledMesh.instanceMatrix.needsUpdate = ledGlow.instanceMatrix.needsUpdate = true;
  parent.add(ledMesh); parent.add(ledGlow);
  view.leds = data;   // memorise les positions LED (export AR)
  applyEyes();
  scheduleBlink();
}

// Yeux pour l'export AR : vraies LED "allumees" en petits cubes emissifs (pas
// d'InstancedMesh, que l'export USDZ ne gere pas). Reflete expression + couleur.
function buildExportEyes() {
  const g = new THREE.Group();
  const data = view.leds;
  if (!data || resolveVersion(state) !== 'Eliobot') return g;
  const emo = EYE_EMOTIONS[state.eyes] || EYE_EMOTIONS['Amoureux'];
  const col = new THREE.Color(LED_COLORS[state.ledColor] || '#3aa6ff');
  const s = data.ledSize * 1.15;
  const geo = new THREE.BoxGeometry(s, s, s);
  // couleur LED en base + emissif (intensite 1 -> pas d'extension exotique, USDZ
  // friendly). La couleur vive suffit a faire ressortir les yeux a travers la visiere.
  const mat = new THREE.MeshStandardMaterial({ color: col, emissive: col, emissiveIntensity: 1, roughness: 0.4 });
  for (const eye of ['left', 'right']) {
    const e = eye === 'left' ? 0 : 1;
    let k = 0;
    for (const p of data[eye]) {
      const r = Math.floor(k / 8), c = k % 8;
      if (emo[fwIndex(e, r, c)] === 1) {
        const box = new THREE.Mesh(geo, mat);
        box.position.set(p[0], p[1], p[2]);
        g.add(box);
      }
      k++;
    }
  }
  return g;
}

// Backpack courant (objet 3D) s'il y en a un, pour l'inclure dans l'export AR.
function getBackpackObject() { return backpackObj; }

function applyEyes() {
  if (!ledMesh) return;
  const isElio = resolveVersion(state) === 'Eliobot';
  ledMesh.visible = isElio; ledGlow.visible = isElio;
  if (!isElio) return;
  const emo = EYE_EMOTIONS[state.eyes] || EYE_EMOTIONS['Amoureux'];
  eyeOn.set(LED_COLORS[state.ledColor] || '#3aa6ff');
  glowCol.copy(eyeOn).multiplyScalar(0.42);  // halo plus doux que le pixel
  let i = 0;
  for (let e = 0; e < 2; e++) for (let r = 0; r < 8; r++) for (let c = 0; c < 8; c++) {
    // Clignement : barre horizontale (lignes 3-4) sur les deux yeux.
    const on = blinkClosed ? (r === 3 || r === 4) : emo[fwIndex(e, r, c)] === 1;
    ledMesh.setColorAt(i, on ? eyeOn : EYE_OFF);
    ledGlow.setColorAt(i, on ? glowCol : BLACK_C);
    i++;
  }
  ledMesh.instanceColor.needsUpdate = true;
  ledGlow.instanceColor.needsUpdate = true;
}

function scheduleBlink() {
  setTimeout(() => {
    if (ledMesh && resolveVersion(state) === 'Eliobot') {
      blinkClosed = true; applyEyes();
      setTimeout(() => { blinkClosed = false; applyEyes(); scheduleBlink(); }, 130);
    } else { scheduleBlink(); }
  }, 2800 + Math.random() * 3500);
}

// Robot de démonstration en primitives (disque carte mère + coques + dôme).
function buildDemoRobot() {
  const group = new THREE.Group();

  const baseMat = new THREE.MeshStandardMaterial({ color: 0x6b7075, roughness: 0.6, metalness: 0.15 });
  const base = new THREE.Mesh(new THREE.CylinderGeometry(1, 1, 0.18, 48), baseMat);
  base.name = NODE_BASE;
  group.add(base);

  const lowerMat = new THREE.MeshStandardMaterial({ roughness: 0.45 });
  const lower = new THREE.Mesh(new THREE.CylinderGeometry(1.02, 0.88, 0.45, 48), lowerMat);
  lower.position.y = -0.32;
  lower.name = NODE_LOWER;
  group.add(lower);

  const upperMat = new THREE.MeshStandardMaterial({ roughness: 0.45 });
  const upper = new THREE.Mesh(new THREE.SphereGeometry(1.02, 48, 32, 0, Math.PI * 2, 0, Math.PI / 2), upperMat);
  upper.position.y = 0.09;
  upper.name = NODE_UPPER;
  group.add(upper);

  const topClear = new THREE.Mesh(
    new THREE.CylinderGeometry(1.02, 1.02, 0.04, 48),
    new THREE.MeshStandardMaterial({ color: 0x9aa0a6, roughness: 0.3, transparent: true, opacity: 0.35 })
  );
  topClear.position.y = 0.12;
  topClear.name = NODE_TOPCLEAR;
  group.add(topClear);

  shellMats.push(lowerMat, upperMat);

  // Roues blanches sur les cotes (toujours visibles, 3 modeles).
  const wheelMat = new THREE.MeshStandardMaterial({ color: 0xf2f2f2, roughness: 0.6 });
  const hubMat = new THREE.MeshStandardMaterial({ color: 0x888888, roughness: 0.5 });
  [-1.05, 1.05].forEach((x) => {
    const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.34, 0.18, 28), wheelMat);
    wheel.rotation.z = Math.PI / 2;
    wheel.position.set(x, -0.42, 0);
    group.add(wheel);
    const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.2, 16), hubMat);
    hub.rotation.z = Math.PI / 2;
    hub.position.set(x, -0.42, 0);
    group.add(hub);
  });

  DATA.eyes.forEach((e, i) => {
    const eye = new THREE.Group();
    eye.name = e.node;
    const white = new THREE.MeshStandardMaterial({ color: 0xffffff });
    const dark = new THREE.MeshStandardMaterial({ color: 0x111111 });
    [-0.32, 0.32].forEach((x) => {
      const sclera = new THREE.Mesh(new THREE.SphereGeometry(0.16, 24, 24), white);
      sclera.position.set(x, 0.5, 0.9);
      eye.add(sclera);
      const pupil = new THREE.Mesh(new THREE.SphereGeometry(0.07, 16, 16), dark);
      pupil.position.set(x + (i - 1) * 0.05, 0.5 + (i === 1 ? 0.05 : 0), 1.02);
      eye.add(pupil);
    });
    group.add(eye);
  });

  const mount = new THREE.Object3D();
  mount.name = BACKPACK_MOUNT;
  mount.position.set(0, 0.1, -1.0);
  group.add(mount);

  group.traverse((o) => { if (o.isMesh) o.castShadow = true; if (o.name) nodes[o.name] = o; });
  scene.add(group);
  loaderEl.classList.add('is-hidden');
  apply3D();
  resize();
}

function loadModel() {
  gltfLoader.load(DATA.assets.model, (gltf) => {
    gltf.scene.traverse((o) => {
      if (o.name) nodes[o.name] = o;
      if (o.name && o.name.startsWith('Eye_')) eyeNodes.push(o);
      if (o.isMesh) o.castShadow = true;
      // Pieces teintees par la couleur choisie (coques + bouton).
      if (o.isMesh && TINT_NODES.includes(o.name)) {
        o.material = o.material.clone();
        shellMats.push(o.material);
      }
    });
    // Fusion exporte l'avant vers -Z : on retourne pour que l'avant fasse face camera (+Z).
    gltf.scene.rotation.y = Math.PI;
    scene.add(gltf.scene);
    view.model = gltf.scene;   // ref pour le fit camera mobile (cadrage sans clip)
    // Pose le sol au contact du bas du modele (roues) au lieu de le laisser flotter.
    const box = new THREE.Box3().setFromObject(gltf.scene);
    shadowPlane.position.y = box.min.y + 0.002;
    // LED des yeux : positions chargees depuis le JSON, ajoutees au modele (tournent avec).
    const ledsUrl = DATA.assets.leds || DATA.assets.model.replace(/\.glb(\?|$)/, '-leds.json$1');
    fetch(ledsUrl).then(r => r.json()).then(d => buildLeds(gltf.scene, d)).catch(e => console.warn('[configurateur] leds', e));
    loaderEl.classList.add('is-hidden');
    apply3D();
    resize();
  }, undefined, (err) => {
    loaderEl.textContent = 'Modele 3D introuvable. Verifie le fichier .glb dans assets/.';
    console.error('[configurateur] echec chargement glb', err);
  });
}

function apply3D() {
  const version = resolveVersion(state);
  setVisible(NODE_BASE, true);
  setVisible(NODE_LOWER, state.lower);
  setVisible(NODE_UPPER, state.upper);
  // Original (coque inf sans coque sup) : couvercle fume translucide.
  setVisible(NODE_TOPCLEAR, state.lower && !state.upper);
  // Bouton : present uniquement sur l'Original (l'Eliobot ne l'a pas).
  setVisible(NODE_BUTTON, version === 'Original');
  // Visiere : Eliobot complet. Backpack par defaut : Eliobot sans backpack choisi
  // (un backpack creatif le remplace).
  setVisible(NODE_VISOR, version === 'Eliobot');
  setVisible(NODE_BACKPACK_DEF, version === 'Eliobot' && !state.backpack);
  // Internes (support moteurs, bumpers, vis) : visibles en Maker uniquement.
  MAKER_NODES.forEach(n => setVisible(n, version === 'Maker'));
  // Module yeux : Eliobot complet uniquement (ni Maker ni Original).
  eyeNodes.forEach(o => { o.visible = version === 'Eliobot'; });

  const hex = COLORS[state.color] || COLORS['Gris'];
  shellMats.forEach(m => m.color && m.color.set(hex));

  // Yeux LED : motif selon l'expression choisie, allumes sur Eliobot complet.
  applyEyes();
  loadBackpack();
}

function loadBackpack() {
  const want = state.backpack;
  if (backpackObj) { backpackObj.parent && backpackObj.parent.remove(backpackObj); backpackObj = null; }
  if (!want) return;
  if (DEMO) {
    backpackObj = buildDemoBackpack(want.shape);
    (nodes[BACKPACK_MOUNT] || scene).add(backpackObj);
    return;
  }
  if (!want.glb) return;
  gltfLoader.load(want.glb, (gltf) => {
    backpackObj = gltf.scene;
    // Pieces teintees de l'accessoire (nom de node prefixe 'Tint') : elles
    // prennent la couleur de coque choisie, comme le robot.
    const hex = COLORS[state.color] || COLORS['Gris'];
    gltf.scene.traverse((o) => {
      if (o.isMesh && o.name && o.name.startsWith('Tint')) {
        o.material = o.material.clone();
        o.material.color && o.material.color.set(hex);
      }
      if (o.isMesh) o.castShadow = true;
    });
    (nodes[BACKPACK_MOUNT] || scene).add(backpackObj);
  });
}

// Backpacks de demonstration (formes distinctes selon shape).
function buildDemoBackpack(shape) {
  const g = new THREE.Group();
  if (shape === 'led') {
    // Panneau matrice de LED colorees.
    g.add(new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.7, 0.12), new THREE.MeshStandardMaterial({ color: 0x222222, roughness: 0.5 })));
    const cols = [0xff4d4d, 0x4dff88, 0x4d9bff, 0xffd24d];
    let k = 0;
    for (let yy = -1; yy <= 1; yy++) for (let xx = -1; xx <= 1; xx++) {
      const c = cols[k++ % 4];
      const led = new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.13, 0.06), new THREE.MeshStandardMaterial({ color: c, emissive: c, emissiveIntensity: 0.8 }));
      led.position.set(xx * 0.22, yy * 0.22, 0.09);
      g.add(led);
    }
  } else if (shape === 'antenne') {
    // Mat + boule radar.
    g.add(new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.25, 0.3), new THREE.MeshStandardMaterial({ color: 0x333333 })));
    const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.7, 16), new THREE.MeshStandardMaterial({ color: 0x555555 }));
    mast.position.y = 0.4;
    g.add(mast);
    const ball = new THREE.Mesh(new THREE.SphereGeometry(0.18, 24, 24), new THREE.MeshStandardMaterial({ color: 0xff5555, roughness: 0.3, emissive: 0x551111, emissiveIntensity: 0.4 }));
    ball.position.y = 0.82;
    g.add(ball);
  } else {
    // 'bras' (defaut) : petit bras articule.
    g.add(new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.3, 0.3), new THREE.MeshStandardMaterial({ color: 0x444444 })));
    const a1 = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.5, 0.12), new THREE.MeshStandardMaterial({ color: 0x888888 }));
    a1.position.set(0, 0.32, 0); a1.rotation.z = 0.4; g.add(a1);
    const a2 = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.42, 0.1), new THREE.MeshStandardMaterial({ color: 0xaaaaaa }));
    a2.position.set(0.27, 0.58, 0); a2.rotation.z = -0.6; g.add(a2);
    const claw = new THREE.Mesh(new THREE.SphereGeometry(0.09, 16, 16), new THREE.MeshStandardMaterial({ color: 0xffaa33 }));
    claw.position.set(0.5, 0.7, 0); g.add(claw);
  }
  return g;
}

function boot() {
  if (DEMO) { loadEnv().then(buildDemoRobot); }
  else { loadEnv().then(loadModel); }
  resize();
  tick();
}

// ----- cfg3d-ar.js -----
// Exporters charges STATIQUEMENT (au chargement de la page, en meme temps que
// three depuis le MEME esm.sh -> instance THREE partagee). Ainsi un blip esm.sh
// PLUS TARD (au tap AR) ne casse plus l'export : tout est deja en memoire. Si
// esm.sh est down au chargement, three ne charge pas et tout le configurateur
// echoue de toute facon (donc pas de regression specifique a l'AR).
import { GLTFExporter } from 'https://esm.sh/three@0.160.0/examples/jsm/exporters/GLTFExporter.js';
import { USDZExporter } from 'https://esm.sh/three@0.160.0/examples/jsm/exporters/USDZExporter.js';

// ====================================================================
//  AR — voir l'Eliobot configuré en realite augmentee.
//  - iOS  : AR Quick Look (USDZ genere par model-viewer depuis le GLB)
//  - Android : AR WebXR / Scene Viewer (model-viewer)
//  - Desktop : QR code vers l'URL de la config (a scanner sur tel)
//  On exporte la scene CONFIGUREE en GLB a la volee -> l'AR reflete
//  couleur des coques + yeux (expression + couleur) + backpack eventuel.
// ====================================================================

// model-viewer + qrcode : autonomes -> jsDelivr (CDN plus fiable qu'esm.sh, qui a
// des coupures intermittentes -> sinon catch "AR pas dispo"). Les exporters three
// DOIVENT rester sur le MEME esm.sh three@0.160.0 que le bundle (instance THREE
// partagee, sinon "deux three" et l'export casse).
const MV_URL = 'https://cdn.jsdelivr.net/npm/@google/model-viewer@3.5.0/+esm';
const QR_URL = 'https://cdn.jsdelivr.net/npm/qrcode@1.5.3/+esm';
const AR_REAL_SIZE = 0.10;   // plus grande dimension reelle = longueur 10 cm (largeur 8,5 cm suit)
const AR_SAT = 1.25;         // boost saturation des coques a l'export AR (Quick Look eclaire fort -> sinon delave)

let overlay = null;          // modale AR (creee a la demande)
let lastBlobUrl = null;
let lastUsdzUrl = null;

// import() resilient : un CDN peut avoir un blip reseau. On reessaie quelques
// fois avant d'abandonner (evite le faux "AR pas dispo" sur coupure passagere).
async function importRetry(url, tries = 3) {
  let err;
  for (let i = 0; i < tries; i++) {
    try { return await import(url); }
    catch (e) { err = e; await new Promise((r) => setTimeout(r, 400 * (i + 1))); }
  }
  throw err;
}

// Construit un groupe exportable (GLB) refletant la config courante.
function buildARModel() {
  const root = new THREE.Group();
  if (!view.model) return root;
  const model = view.model.clone(true);
  // Les LED sont des InstancedMesh (mal gerees a l'export) : on les retire et on
  // remet de vrais cubes emissifs via buildExportEyes().
  const drop = [];
  model.traverse((o) => { if (o.isInstancedMesh) drop.push(o); });
  drop.forEach((o) => o.parent && o.parent.remove(o));
  model.add(buildExportEyes());
  const bp = getBackpackObject();
  if (bp) model.add(bp.clone(true));
  root.add(model);

  // Quick Look (iOS) eclaire tres fort -> les coques paraissent delavees. On
  // re-sature un peu les materiaux a l'export (CLONE obligatoire : view.model.
  // clone(true) PARTAGE les materiaux, il ne faut surtout pas toucher au live).
  // Les yeux LED (materiaux emissifs) sont laisses intacts.
  const boost = (mat) => {
    const emissive = mat.emissive && (mat.emissive.r + mat.emissive.g + mat.emissive.b) > 0;
    if (emissive && mat.emissiveIntensity > 0) return mat;     // yeux LED : on garde
    const m = mat.clone();
    if (m.color) {
      const hsl = {}; m.color.getHSL(hsl);
      m.color.setHSL(hsl.h, Math.min(1, hsl.s * AR_SAT), hsl.l * 0.97);
    }
    if (typeof m.roughness === 'number') m.roughness = Math.max(0.05, m.roughness * 0.85);
    return m;
  };
  root.traverse((o) => {
    if (!o.isMesh || !o.material) return;
    // PRESERVER la structure : un materiau simple ne doit PAS devenir un tableau,
    // sinon GLTFExporter lit material[groupIndex]=undefined -> crash isShaderMaterial.
    o.material = Array.isArray(o.material) ? o.material.map(boost) : boost(o.material);
  });

  // Echelle reelle + pose au sol (y=0 sous les roues).
  const box = new THREE.Box3().setFromObject(root);
  const size = box.getSize(new THREE.Vector3());
  const s = AR_REAL_SIZE / (Math.max(size.x, size.y, size.z) || 1);
  root.scale.setScalar(s);
  root.updateMatrixWorld(true);
  const box2 = new THREE.Box3().setFromObject(root);
  root.position.y -= box2.min.y;
  return root;
}

async function exportGLBUrl() {
  const glb = await new GLTFExporter().parseAsync(buildARModel(), { binary: true });
  if (lastBlobUrl) URL.revokeObjectURL(lastBlobUrl);
  lastBlobUrl = URL.createObjectURL(new Blob([glb], { type: 'model/gltf-binary' }));
  return lastBlobUrl;
}

// iOS exige un USDZ pour AR Quick Look. On le genere NOUS-MEMES (USDZExporter)
// plutot que de laisser model-viewer convertir le GLB a la volee (conversion
// auto peu fiable -> "le bouton reagit mais rien ne s'ouvre").
async function exportUSDZUrl() {
  const usdz = await new USDZExporter().parse(buildARModel(), {
    quickLookCompatible: true,
    maxTextureSize: 1024,
    ar: { anchoring: { type: 'plane' }, planeAnchoring: { alignment: 'horizontal' } },
  });
  if (lastUsdzUrl) URL.revokeObjectURL(lastUsdzUrl);
  lastUsdzUrl = URL.createObjectURL(new Blob([usdz], { type: 'model/vnd.usdz+zip' }));
  return lastUsdzUrl;
}

function isMobile() { return window.matchMedia('(max-width: 880px)').matches; }
function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}
// iOS reserve l'AR Quick Look a Safari : Chrome/Firefox/Edge/Opera/Google/DuckDuckGo
// sur iPhone ne peuvent PAS la lancer -> on les detecte pour afficher "ouvre dans Safari".
function isIOSNonSafari() {
  return isIOS() && /CriOS|FxiOS|EdgiOS|OPiOS|GSA|DuckDuckGo/.test(navigator.userAgent);
}

function buildOverlay() {
  overlay = document.createElement('div');
  overlay.className = 'e-ar';
  overlay.innerHTML = `
    <div class="e-ar__sheet" role="dialog" aria-label="Voir en réalité augmentée">
      <button type="button" class="e-ar__close" aria-label="Fermer">&times;</button>
      <div class="e-ar__body"></div>
    </div>`;
  overlay.addEventListener('click', (e) => { if (e.target === overlay) closeOverlay(); });
  overlay.querySelector('.e-ar__close').addEventListener('click', closeOverlay);
  // attache a .e-cfg-theme (herite des tokens couleur ; pas de transform dessus
  // donc le position:fixed reste relatif au viewport).
  (document.querySelector('.e-cfg-theme') || document.body).appendChild(overlay);
  return overlay;
}
function openOverlay() { (overlay || buildOverlay()).classList.add('is-open'); document.body.style.overflow = 'hidden'; }
function closeOverlay() { if (overlay) overlay.classList.remove('is-open'); document.body.style.overflow = ''; }

// --- Mobile : modale avec apercu 3D (model-viewer) + bouton AR ---
async function showMobileAR() {
  openOverlay();
  const body = overlay.querySelector('.e-ar__body');
  // iOS hors Safari : l'AR ne peut pas se lancer -> message direct (pas de preview
  // ni de bouton inutile, et on ne charge ni model-viewer ni USDZ).
  if (isIOSNonSafari()) {
    body.innerHTML = '<h3 class="e-ar__title">Ouvre dans Safari</h3>'
      + '<p class="e-ar__hint">Sur iPhone, voir Eliobot en réalité augmentée ne fonctionne que dans '
      + '<b>Safari</b>. Ouvre cette page dans Safari (menu du navigateur &rarr; « Ouvrir dans Safari »).</p>';
    return;
  }
  // Android (non-iOS) : notre AR passe par WebXR (le modele est un blob genere a la
  // volee ; Scene Viewer exigerait une URL publique qu'on n'a pas). Si WebXR
  // immersive-ar n'est pas dispo (ex. Firefox Android), on oriente vers Chrome
  // au lieu d'un bouton qui lance Scene Viewer avec un blob -> "n'importe quoi".
  if (!isIOS()) {
    let xrAR = false;
    try { xrAR = !!(navigator.xr && await navigator.xr.isSessionSupported('immersive-ar')); } catch (e) {}
    if (!xrAR) {
      body.innerHTML = '<h3 class="e-ar__title">Ouvre dans Chrome</h3>'
        + '<p class="e-ar__hint">Sur Android, voir Eliobot en réalité augmentée fonctionne dans '
        + '<b>Chrome</b> (récent, avec les services Google pour la RA). Ouvre cette page dans Chrome.</p>';
      return;
    }
  }
  body.innerHTML = '<div class="e-ar__loading">Préparation de la 3D…</div>';
  const ios = isIOS();
  try {
    // On prepare TOUT a l'ouverture : le GLB pour l'apercu, le model-viewer, et
    // (sur iOS) le USDZ pour Quick Look -> au tap, plus rien d'async, le geste
    // utilisateur est intact et l'AR se lance immediatement.
    const jobs = [exportGLBUrl(), importRetry(MV_URL)];
    if (ios) jobs.push(exportUSDZUrl());
    const [glbSrc, , usdzSrc] = await Promise.all(jobs);
    body.innerHTML = `
      <model-viewer class="e-ar__mv" src="${glbSrc}"${ios && usdzSrc ? ` ios-src="${usdzSrc}"` : ''}
        ar ar-modes="webxr quick-look"
        ar-scale="fixed" camera-controls touch-action="pan-y" shadow-intensity="0.9"
        camera-orbit="35deg 75deg auto" exposure="0.55" tone-mapping="neutral" interaction-prompt="none"></model-viewer>
      <button type="button" class="e-ar__go" disabled>📱 Poser dans ma pièce</button>
      <p class="e-ar__hint">Bouge ton téléphone pour poser l'Eliobot autour de toi.</p>`;
    const mv = body.querySelector('model-viewer');
    const go = body.querySelector('.e-ar__go');
    const hint = body.querySelector('.e-ar__hint');
    // Safari (iOS) + Android : on laisse model-viewer lancer l'AR (Quick Look via
    // notre ios-src sur iOS, WebXR/Scene Viewer sur Android). Pas de message Safari
    // ici (le cas hors Safari est traite en amont).
    const enable = () => { go.disabled = false; };
    if (mv.loaded) enable(); else mv.addEventListener('load', enable, { once: true });
    go.addEventListener('click', () => {
      if (mv.canActivateAR) { mv.activateAR(); return; }
      hint.textContent = "L'AR n'est pas disponible sur ce navigateur. Ouvre la page sur iPhone (Safari) ou Android récent (Chrome).";
    });
  } catch (e) {
    console.error('[configurateur] AR', e);
    body.innerHTML = '<p class="e-ar__hint">L\'AR n\'a pas pu se charger (connexion ?). Ferme et rouvre pour réessayer.</p>';
  }
}

// --- Desktop : QR vers l'URL de la config (a ouvrir sur smartphone) ---
async function showDesktopQR() {
  openOverlay();
  const body = overlay.querySelector('.e-ar__body');
  body.innerHTML = '<div class="e-ar__loading">Génération du QR…</div>';
  try {
    const QR = await importRetry(QR_URL);
    const dataUrl = await (QR.toDataURL || QR.default.toDataURL)(location.href, { width: 240, margin: 1, color: { dark: '#1C1B22', light: '#FFFFFF' } });
    body.innerHTML = `
      <h3 class="e-ar__title">Voir Eliobot en vrai</h3>
      <p class="e-ar__hint">Scanne ce QR code avec ton téléphone pour ouvrir cette configuration et la projeter chez toi en réalité augmentée.</p>
      <img class="e-ar__qr" src="${dataUrl}" alt="QR code vers la configuration" width="240" height="240">`;
  } catch (e) {
    console.error('[configurateur] QR', e);
    body.innerHTML = '<p class="e-ar__hint">Impossible de générer le QR code.</p>';
  }
}

function initAR() {
  const btn = document.querySelector('[data-cfg-ar]');
  if (!btn) return;
  btn.addEventListener('click', () => { isMobile() ? showMobileAR() : showDesktopQR(); });
}

// ----- cfg3d-ui.js -----
// .e-cfg-theme porte les classes sheet-open / sheet-closed (volet mobile).
const themeRoot = root.closest('.e-cfg-theme') || document.querySelector('.e-cfg-theme');

const ui = {
  handle: root.querySelector('[data-cfg-handle]'),
  handleLabel: root.querySelector('[data-cfg-handle-label]'),
  lower: root.querySelector('[data-cfg-lower]'),
  upper: root.querySelector('[data-cfg-upper]'),
  version: root.querySelector('[data-cfg-version]'),
  colorGroup: root.querySelector('[data-cfg-color-group]'),
  colors: root.querySelector('[data-cfg-colors]'),
  eyesSelect: root.querySelector('[data-cfg-eyes-select]'),
  eyesGroup: root.querySelector('[data-cfg-eyes-group]'),
  ledColors: root.querySelector('[data-cfg-ledcolors]'),
  ledColorGroup: root.querySelector('[data-cfg-ledcolor-group]'),
  backpacks: root.querySelector('[data-cfg-backpacks]'),
  backpackGroup: root.querySelector('[data-cfg-backpack-group]'),
  price: root.querySelector('[data-cfg-price]'),
  add: root.querySelector('[data-cfg-add]'),
  share: root.querySelector('[data-cfg-share]'),
};

function buildSwatches() {
  COLOR_ORDER.forEach(name => {
    const b = document.createElement('button');
    b.className = 'cfg__swatch';
    b.type = 'button';
    b.style.background = COLORS[name];
    b.title = name;
    b.setAttribute('aria-label', name);
    b.dataset.color = name;
    b.innerHTML = '<span class="cfg__swatch-check">✓</span>';
    b.addEventListener('click', () => { state.color = name; sync(); });
    ui.colors.appendChild(b);
  });
}
function buildLedSwatches() {
  if (!ui.ledColors) return;
  LED_COLOR_ORDER.forEach(name => {
    const b = document.createElement('button');
    b.className = 'cfg__swatch';
    b.type = 'button';
    b.style.background = LED_COLORS[name];
    b.title = name;
    b.setAttribute('aria-label', name);
    b.dataset.led = name;
    b.innerHTML = '<span class="cfg__swatch-check">✓</span>';
    b.addEventListener('click', () => { state.ledColor = name; sync(); });
    ui.ledColors.appendChild(b);
  });
}
function buildChips(container, items, getLabel, onPick, getNote) {
  items.forEach(it => {
    const b = document.createElement('button');
    b.className = 'cfg__chip';
    b.type = 'button';
    b.dataset.label = getLabel(it);
    if (getNote) {
      b.classList.add('cfg__chip--bp');
      const name = document.createElement('span');
      name.className = 'cfg__chip-name';
      name.textContent = getLabel(it);
      const note = document.createElement('span');
      note.className = 'cfg__chip-note';
      note.textContent = getNote(it);
      b.append(name, note);
    } else {
      b.textContent = getLabel(it);
    }
    b.addEventListener('click', () => onPick(it));
    container.appendChild(b);
  });
}

function sync() {
  ui.upper.disabled = !state.lower;
  if (!state.lower) state.upper = false;
  ui.lower.checked = state.lower;
  ui.upper.checked = state.upper;

  const version = resolveVersion(state);
  if (ui.version) ui.version.textContent = 'Version : ' + version;
  // Groupes conditionnels : collapse animé (classe Design) au lieu de display.
  ui.colorGroup.classList.toggle('is-collapsed', version === 'Maker');
  if (ui.eyesGroup) ui.eyesGroup.classList.toggle('is-collapsed', version !== 'Eliobot');
  if (ui.ledColorGroup) ui.ledColorGroup.classList.toggle('is-collapsed', version !== 'Eliobot');
  // Backpacks compatibles uniquement avec l'Eliobot complet.
  if (ui.backpackGroup) {
    if (version !== 'Eliobot') state.backpack = null;
    ui.backpackGroup.classList.toggle('is-collapsed', version !== 'Eliobot');
  }

  ui.colors.querySelectorAll('.cfg__swatch').forEach(s =>
    s.classList.toggle('is-active', s.dataset.color === state.color));
  if (ui.eyesSelect && ui.eyesSelect.value !== state.eyes) ui.eyesSelect.value = state.eyes;
  if (ui.ledColors) ui.ledColors.querySelectorAll('.cfg__swatch').forEach(s =>
    s.classList.toggle('is-active', s.dataset.led === state.ledColor));
  if (ui.backpacks) ui.backpacks.querySelectorAll('.cfg__chip').forEach(c =>
    c.classList.toggle('is-active', state.backpack ? c.dataset.label === state.backpack.label : c.dataset.label === 'Aucun'));

  const v = findVariant();
  let total = v ? v.price : 0;
  if (state.backpack) total += state.backpack.price;
  ui.price.textContent = formatMoney(total);
  ui.add.disabled = !v || !v.available;
  ui.add.textContent = (v && !v.available) ? 'Rupture' : 'Ajouter au panier';

  apply3D();
  writeUrl();
}

async function addToCart() {
  const v = findVariant();
  if (!v) return;
  const cfgId = 'elio-' + v.id + '-' + (state.color || 'na');
  const props = { '_config': cfgId, 'Version': resolveVersion(state) };
  if (resolveVersion(state) !== 'Maker') props['Couleur'] = state.color;
  if (resolveVersion(state) === 'Eliobot' && state.eyes) props['Yeux'] = state.eyes;
  if (resolveVersion(state) === 'Eliobot' && state.ledColor) props['Couleur des yeux'] = state.ledColor;

  const items = [{ id: v.id, quantity: 1, properties: props }];
  if (state.backpack) {
    // Bolide et Chat existent dans les 7 couleurs du robot : on prend la
    // variante qui correspond a la coque choisie. Briques n'a qu'une variante.
    const bpVariants = state.backpack.variants || [];
    const bpVariant = bpVariants.find((v) => v.couleur === state.color);
    const bpId = bpVariant ? bpVariant.id : state.backpack.variantId;
    items.push({ id: bpId, quantity: 1, properties: { '_config': cfgId } });
  }

  const delta = items.reduce((n, i) => n + i.quantity, 0);
  ui.add.disabled = true;
  ui.add.textContent = 'Ajout...';
  try {
    // Theme moderne (Pebble) : on rejoue son flux natif d'ajout panier.
    // Sinon (theme sans ce module) : repli sur la page /cart.
    let evMod = null;
    try { evMod = await import('@theme/events'); } catch (_) { /* pas de module theme */ }

    // 1. Demander aux composants quelles sections ils veulent re-rendre.
    let sectionIds = [];
    if (evMod && evMod.CartGroupedSections) {
      const grouped = new evMod.CartGroupedSections([]);
      document.dispatchEvent(grouped);
      sectionIds = grouped.detail.sections || [];
    }

    // 2. Ajouter au panier (avec rendu des sections demandees).
    const body = { items };
    if (sectionIds.length) { body.sections = sectionIds; body.sections_url = window.location.pathname; }
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await res.text());
    const parsed = await res.json();

    // 3. Annoncer l'ajout : le tiroir natif s'auto-ouvre et se met a jour.
    if (evMod && evMod.CartAddEvent) {
      document.dispatchEvent(new evMod.CartAddEvent({}, 'configurateur-3d', {
        source: 'configurateur-3d', itemCount: delta, sections: parsed.sections,
      }));
      ui.add.disabled = false;
      ui.add.textContent = 'Ajouter au panier';
    } else {
      window.location.href = '/cart';
    }
  } catch (e) {
    console.error('[configurateur] add to cart', e);
    ui.add.disabled = false;
    ui.add.textContent = 'Erreur, reessayer';
  }
}

function writeUrl() {
  const payload = { l: state.lower, u: state.upper, c: state.color, e: state.eyes, lc: state.ledColor, b: state.backpack ? state.backpack.label : null };
  const enc = btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
  history.replaceState(null, '', '?c=' + enc);
}
function readUrl() {
  const enc = new URLSearchParams(location.search).get('c');
  if (!enc) return;
  try {
    const p = JSON.parse(decodeURIComponent(escape(atob(enc))));
    state.lower = !!p.l;
    state.upper = !!p.u;
    if (p.c && COLORS[p.c]) state.color = p.c;
    if (p.e && EYE_ORDER.includes(p.e)) state.eyes = p.e;
    if (p.lc && LED_COLORS[p.lc]) state.ledColor = p.lc;
    state.backpack = (DATA.backpacks || []).find(x => x.label === p.b) || null;
  } catch (e) { /* lien corrompu : on ignore */ }
}
async function share() {
  const url = location.href;
  try {
    await navigator.clipboard.writeText(url);
    ui.share.textContent = 'Lien copie';
    setTimeout(() => ui.share.textContent = 'Copier le lien (idee cadeau)', 2000);
  } catch (e) {
    prompt('Copie ce lien :', url);
  }
}

function init() {
  buildSwatches();
  buildLedSwatches();
  if (ui.eyesSelect) {
    EYE_ORDER.forEach(name => {
      const o = document.createElement('option');
      o.value = name; o.textContent = name;
      ui.eyesSelect.appendChild(o);
    });
    if (!EYE_ORDER.includes(state.eyes)) state.eyes = EYE_ORDER[0];
    ui.eyesSelect.value = state.eyes;
    ui.eyesSelect.addEventListener('change', () => { state.eyes = ui.eyesSelect.value; sync(); });
  }
  if (ui.backpacks) {
    const none = { label: 'Aucun' };
    buildChips(ui.backpacks, [none, ...DATA.backpacks],
      b => b.label,
      b => { state.backpack = b === none ? null : b; sync(); },
      b => b.price ? '+' + Math.round(b.price / 100) + ' €' : 'Inclus');
  }

  ui.lower.addEventListener('change', () => { state.lower = ui.lower.checked; sync(); });
  ui.upper.addEventListener('change', () => { state.upper = ui.upper.checked; sync(); });
  ui.add.addEventListener('click', addToCart);
  ui.share.addEventListener('click', share);

  // Volet mobile : la poignée déplie/replie les options (sans effet en desktop,
  // où la poignée est masquée et les classes sheet-* ignorées par le CSS).
  // Inviter à scroller : dégradé en bas du tiroir, masqué une fois arrivé en bas.
  const scrollEl = root.querySelector('.e-cfg__scroll');
  function updateScrollEnd() {
    if (!scrollEl) return;
    const atEnd = scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 4;
    scrollEl.classList.toggle('at-end', atEnd);
  }
  if (scrollEl) scrollEl.addEventListener('scroll', updateScrollEnd, { passive: true });

  if (ui.handle && themeRoot) {
    ui.handle.addEventListener('click', () => {
      const open = themeRoot.classList.toggle('sheet-open');
      themeRoot.classList.toggle('sheet-closed', !open);
      if (ui.handleLabel) ui.handleLabel.textContent = open ? 'Réduire' : 'Personnaliser';
      themeRoot.classList.remove('sheet-invite');   // l'utilisateur a interagi -> stop invite
      // Mobile : on masque le header du site quand on personnalise (classe sur <html>,
      // le CSS scopé page cache .header-section) pour rendre la 3D au modèle. Puis on
      // recale la hauteur de la carte sur l'espace libéré.
      document.documentElement.classList.toggle('cfg-sheet-open', open);
      fitCardHeight();
      // À l'ouverture, on cadre le configurateur plein écran (header masqué) quel
      // que soit le scroll de départ, pour que la 3D occupe bien le haut.
      if (open) window.scrollTo({ top: 0 });
      if (open) setTimeout(updateScrollEnd, 480);    // met à jour l'état du dégradé une fois déplié
    });
    // Au chargement mobile (volet fermé), petit mouvement d'invitation à ouvrir.
    if (window.matchMedia('(max-width: 880px)').matches) {
      setTimeout(() => {
        if (!themeRoot.classList.contains('sheet-closed')) return;   // déjà ouvert -> on n'invite pas
        themeRoot.classList.add('sheet-invite');
        setTimeout(() => themeRoot.classList.remove('sheet-invite'), 2200);
      }, 800);
    }
  }

  initAR();
  readUrl();
  sync();
  boot();
}

init();
